import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { I, Icon } from '../shared/ns-icons'
import '../../styles/client-pages.css'

/* WebNest — client portal: Orders (card orders placed through the client's website).
   Ported from the redesign prototype and wired to the real /api/orders backend.
   Fields the backend doesn't capture (fulfilment, tax/tip, card brand, address,
   customer note) are intentionally omitted. */

// ── Status → badge tone/label ─────────────────────────────────────────────────
const ORD_STATUS = {
  pending:            { tone: 'warning', label: 'Pending payment' },
  paid:               { tone: 'success', label: 'Paid' },
  partially_refunded: { tone: 'info',    label: 'Part-refunded' },
  refunded:           { tone: 'default', label: 'Refunded' },
  cancelled:          { tone: 'danger',  label: 'Cancelled' },
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const money = (pence, currency = 'gbp') => {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: (currency || 'gbp').toUpperCase() }).format((Number(pence) || 0) / 100)
  } catch {
    return `£${((Number(pence) || 0) / 100).toFixed(2)}`
  }
}
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'
const fmtFull = (d) => d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
const isToday = (d) => { const t = new Date(d), n = new Date(); return t.getDate() === n.getDate() && t.getMonth() === n.getMonth() && t.getFullYear() === n.getFullYear() }
const shortId = (id) => '#' + String(id || '').replace(/-/g, '').slice(0, 8).toUpperCase()
const initialsOf = (name) => (name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'
const gradOf = (id) => ((String(id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 6) + 1
const itemQty = (items) => (items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0)

function ClientOrders() {
  const queryClient = useQueryClient()
  const [activeId, setActiveId] = useState(null)
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [modal, setModal] = useState(null) // { type: 'cancel'|'refund', id }

  const { data, isLoading } = useQuery('client-orders', () => apiService.get('/orders').then(r => r.data))
  const orders = data?.orders || []

  const invalidate = () => queryClient.invalidateQueries('client-orders')

  const cancelMutation = useMutation(
    ({ id, reason }) => apiService.post(`/orders/${id}/cancel`, { reason }),
    {
      onSuccess: (_r, v) => { toast.success(`Order ${shortId(v.id)} cancelled`); setModal(null); invalidate() },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to cancel order'),
    }
  )
  const refundMutation = useMutation(
    ({ id, amount, reason }) => apiService.post(`/orders/${id}/refund`, { amount, reason }),
    {
      onSuccess: (r) => { toast.success(`${money(r.data?.refunded_amount, activeOrder?.currency)} refunded`); setModal(null); invalidate() },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to issue refund'),
    }
  )

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      const matchFilter =
        filter === 'all' ? true :
        filter === 'paid' ? o.status === 'paid' :
        filter === 'refunded' ? ['refunded', 'partially_refunded'].includes(o.status) :
        filter === 'cancelled' ? o.status === 'cancelled' :
        filter === 'pending' ? o.status === 'pending' : true
      const q = query.trim().toLowerCase()
      const matchQ = !q
        || (o.customer_name || '').toLowerCase().includes(q)
        || shortId(o.id).toLowerCase().includes(q)
        || (o.items || []).some((it) => (it.name || '').toLowerCase().includes(q))
      return matchFilter && matchQ
    })
  }, [orders, filter, query])

  const activeOrder = orders.find((o) => o.id === activeId) || filtered[0] || orders[0] || null

  // Snapshot stats
  const todays = orders.filter(o => isToday(o.created_at))
  const revenue = todays
    .filter(o => ['paid', 'partially_refunded'].includes(o.status))
    .reduce((s, o) => s + ((Number(o.amount_total) || 0) - (Number(o.refunded_amount) || 0)), 0)
  const pendingCount = orders.filter(o => o.status === 'pending').length
  const refundedList = orders.filter(o => ['refunded', 'partially_refunded'].includes(o.status))
  const refundedSum = refundedList.reduce((s, o) => s + (Number(o.refunded_amount) || 0), 0)

  const counts = {
    all: orders.length,
    paid: orders.filter(o => o.status === 'paid').length,
    refunded: refundedList.length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  }

  const exportCsv = () => {
    const rows = [['Order', 'Date', 'Customer', 'Phone', 'Items', 'Total', 'Status']]
    filtered.forEach(o => rows.push([
      shortId(o.id), fmtFull(o.created_at), o.customer_name || '', o.customer_phone || '',
      (o.items || []).map(it => `${it.quantity}x ${it.name}`).join('; '),
      money(o.amount_total, o.currency), (ORD_STATUS[o.status]?.label || o.status),
    ]))
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a'); a.href = url; a.download = 'orders.csv'; a.click(); URL.revokeObjectURL(url)
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Your store</div>
          <h1>Orders</h1>
          <div className="subtitle">Card orders placed through your website — view, cancel or refund any order.</div>
        </div>
        <div className="flex gap-2 items-center">
          <span className="badge"><span className="dot-live" style={{ marginRight: 5 }} /> Live · taking orders</span>
          <button className="btn" onClick={exportCsv}><I.upload /> Export</button>
        </div>
      </div>

      <div className="page-body">
        {/* Snapshot */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          <OrdStat icon="bag" tone="accent" label="Orders today" value={todays.length} sub="placed via website" />
          <OrdStat icon="dollar" tone="success" label="Revenue today" value={money(revenue, data?.orders?.[0]?.currency)} sub="net of refunds" />
          <OrdStat icon="bell" tone="warning" label="Pending payment" value={pendingCount} sub="checkout not completed" />
          <OrdStat icon="refund" tone="default" label="Refunds" value={refundedList.length} sub={`${money(refundedSum)} returned`} />
        </div>

        {isLoading ? (
          <div className="card card-pad" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>Loading orders…</div>
        ) : orders.length === 0 ? (
          <div className="card card-pad" style={{ textAlign: 'center', padding: '54px 20px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 26, color: 'var(--text-faint)', marginBottom: 10 }}><I.bag /></div>
            <div style={{ fontWeight: 600, color: 'var(--text-soft)', marginBottom: 4 }}>No orders yet</div>
            <div style={{ fontSize: 13 }}>Card orders from your website will appear here. Make sure Stripe is connected in Settings → Billing.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 14, alignItems: 'stretch', minHeight: 560 }}>
            {/* List */}
            <div className="card card-pad-sm" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div className="filter-bar" style={{ border: 'none', padding: '4px 6px 8px' }}>
                <I.search style={{ color: 'var(--text-muted)', fontSize: 15 }} />
                <input className="input" placeholder="Search orders, customers…" value={query} onChange={(e) => setQuery(e.target.value)} />
              </div>

              <div className="tabs" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
                {[
                  { id: 'all', label: 'All' },
                  { id: 'paid', label: 'Paid' },
                  { id: 'refunded', label: 'Refunded' },
                  { id: 'cancelled', label: 'Cancelled' },
                ].map(t => (
                  <button key={t.id} className={filter === t.id ? 'active' : ''} onClick={() => setFilter(t.id)}>
                    {t.label}<span style={{ marginLeft: 5, opacity: 0.6, fontFamily: 'var(--font-mono)', fontSize: 11 }}>{counts[t.id]}</span>
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', paddingRight: 2 }}>
                {filtered.length === 0 && (
                  <div style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12.5 }}>No orders match.</div>
                )}
                {filtered.map(o => {
                  const b = ORD_STATUS[o.status] || { tone: 'default', label: o.status }
                  const isActive = activeOrder && o.id === activeOrder.id
                  return (
                    <button key={o.id} onClick={() => setActiveId(o.id)}
                      style={{
                        textAlign: 'left', padding: 13, borderRadius: 12,
                        border: `1px solid ${isActive ? 'var(--border-accent)' : 'var(--border)'}`,
                        background: isActive ? 'var(--accent-soft)' : 'var(--surface)',
                        transition: 'all 0.14s ease',
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span className={`badge ${b.tone === 'default' ? '' : `badge-${b.tone}`}`} style={{ fontSize: 10 }}>
                          {b.tone !== 'default' && <span className="badge-dot" />} {b.label}
                        </span>
                        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{fmtTime(o.created_at)}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <div className={`avatar avatar-grad-${gradOf(o.id)}`} style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>{initialsOf(o.customer_name)}</div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.customer_name || 'Guest'}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {itemQty(o.items)} item{itemQty(o.items) === 1 ? '' : 's'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{money(o.amount_total, o.currency)}</div>
                          <div style={{ fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{shortId(o.id)}</div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Detail */}
            {activeOrder && (
              <OrderDetail
                o={activeOrder}
                allOrders={orders}
                onCancel={() => setModal({ type: 'cancel', id: activeOrder.id })}
                onRefund={() => setModal({ type: 'refund', id: activeOrder.id })}
              />
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {modal?.type === 'cancel' && (
        <CancelModal
          o={orders.find(o => o.id === modal.id)}
          busy={cancelMutation.isLoading}
          onClose={() => setModal(null)}
          onConfirm={(id, reason) => cancelMutation.mutate({ id, reason })}
        />
      )}
      {modal?.type === 'refund' && (
        <RefundModal
          o={orders.find(o => o.id === modal.id)}
          busy={refundMutation.isLoading}
          onClose={() => setModal(null)}
          onConfirm={(id, amount, reason) => refundMutation.mutate({ id, amount, reason })}
        />
      )}

      <style>{`
        @keyframes ordModalIn { from { opacity: 0; transform: translateY(12px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
    </>
  )
}

/* ============ Detail panel ============ */
function OrderDetail({ o, allOrders, onCancel, onRefund }) {
  const b = ORD_STATUS[o.status] || { tone: 'default', label: o.status }
  const canCancel = ['pending', 'paid'].includes(o.status)
  const canRefund = o.status === 'paid'
  const resolved = ['cancelled', 'refunded', 'partially_refunded'].includes(o.status)
  const prevOrders = allOrders.filter(x => x.id !== o.id && (
    o.customer_phone ? x.customer_phone === o.customer_phone : x.customer_name === o.customer_name
  )).length

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* head */}
      <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div className={`avatar avatar-grad-${gradOf(o.id)}`} style={{ width: 44, height: 44, fontSize: 15, flexShrink: 0 }}>{initialsOf(o.customer_name)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
            <span className={`badge ${b.tone === 'default' ? '' : `badge-${b.tone}`}`}>
              {b.tone !== 'default' && <span className="badge-dot" />} {b.label}
            </span>
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>{o.customer_name || 'Guest'}</h2>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 3 }}>
            {shortId(o.id)} · Placed {fmtFull(o.created_at)}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em' }}>{money(o.amount_total, o.currency)}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>Card payment</div>
        </div>
      </div>

      {/* body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Resolution banner */}
        {resolved && (
          <div style={{
            display: 'flex', gap: 12, padding: '13px 15px', borderRadius: 12,
            background: o.status === 'cancelled' ? 'var(--danger-soft)' : 'var(--surface)',
            border: `1px solid ${o.status === 'cancelled' ? 'transparent' : 'var(--border)'}`,
          }}>
            <span className={`icon-tile ${o.status === 'cancelled' ? 'danger' : ''}`} style={{ width: 30, height: 30, fontSize: 14, flexShrink: 0 }}>
              {o.status === 'cancelled' ? <I.ban /> : <I.refund />}
            </span>
            <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--text)' }}>
                {o.status === 'cancelled'
                  ? 'Order cancelled'
                  : `${money(o.refunded_amount, o.currency)} ${o.status === 'partially_refunded' ? 'partially ' : ''}refunded`}
              </strong>
              {o.resolution_reason && <span style={{ color: 'var(--text-muted)' }}> · {o.resolution_reason}</span>}
              {(o.resolved_at || o.resolved_by) && (
                <div style={{ color: 'var(--text-faint)', fontSize: 11.5, marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                  {fmtFull(o.resolved_at)}{o.resolved_by ? ` · by ${o.resolved_by}` : ''}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Customer + meta */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <MetaTile icon="phone" label="Contact" value={o.customer_phone || '—'} sub={`${prevOrders} previous order${prevOrders === 1 ? '' : 's'}`} />
          <MetaTile icon="clock" label="Order placed" value={fmtFull(o.created_at)} sub="via website checkout" />
        </div>

        {/* Items */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Order items</div>
          <div className="card" style={{ background: 'var(--surface)', padding: '4px 14px' }}>
            {(o.items || []).map((it, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 0', borderBottom: i < o.items.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)', minWidth: 22 }}>{it.quantity}×</span>
                <div style={{ flex: 1, fontSize: 13, color: 'var(--text)' }}>{it.name}</div>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-soft)' }}>{money((Number(it.unit_amount) || 0) * (Number(it.quantity) || 0), o.currency)}</span>
              </div>
            ))}
            {(!o.items || o.items.length === 0) && (
              <div style={{ padding: '14px 0', fontSize: 12.5, color: 'var(--text-muted)' }}>No item details recorded.</div>
            )}
          </div>

          {/* Total */}
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 7, padding: '0 2px' }}>
            <TotalRow label="Total" value={money(o.amount_total, o.currency)} bold />
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div style={{ padding: 16, borderTop: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 10 }}>
        {o.customer_phone && <a className="btn btn-sm" href={`tel:${o.customer_phone}`}><I.phone /> Contact</a>}
        <div style={{ flex: 1 }} />
        {resolved ? (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <I.check style={{ color: 'var(--success)' }} /> Resolved — no further action
          </span>
        ) : (
          <>
            {canCancel && <button className="btn btn-sm" style={ordDangerBtn} onClick={onCancel}><I.ban /> Cancel order</button>}
            {canRefund && <button className="btn btn-sm btn-primary" onClick={onRefund}><I.refund /> Issue refund</button>}
          </>
        )}
      </div>
    </div>
  )
}

const ordDangerBtn = { color: 'var(--danger)', borderColor: 'var(--danger-soft)', background: 'var(--danger-soft)' }

function MetaTile({ icon, label, value, sub }) {
  return (
    <div style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
        <span className="icon-tile" style={{ width: 22, height: 22, fontSize: 11 }}><Icon name={icon} /></span>
        <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function TotalRow({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: bold ? 14 : 12.5 }}>
      <span style={{ color: bold ? 'var(--text)' : 'var(--text-muted)', fontWeight: bold ? 600 : 400 }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-mono)', color: bold ? 'var(--text)' : 'var(--text-soft)', fontWeight: bold ? 600 : 400 }}>{value}</span>
    </div>
  )
}

function OrdStat({ icon, tone, label, value, sub }) {
  return (
    <div className="stat-chip">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className={`icon-tile ${tone === 'default' ? '' : tone}`} style={{ width: 26, height: 26, fontSize: 12 }}><Icon name={icon} /></span>
        <div className="lbl" style={{ marginTop: 0 }}>{label}</div>
      </div>
      <div className="num" style={{ marginTop: 8 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

/* ============ Modal shell ============ */
function OrdModalShell({ children, onClose, width = 460 }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(5,6,12,0.55)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width, maxWidth: '94vw', boxShadow: 'var(--shadow-lg)', animation: 'ordModalIn 0.25s cubic-bezier(0.16,1,0.3,1)' }}>
        {children}
      </div>
    </div>
  )
}

/* ============ Cancel modal ============ */
function CancelModal({ o, onClose, onConfirm, busy }) {
  const reasons = ['Out of stock / sold out', 'Kitchen too busy', 'Customer requested', 'Suspected fraud', 'Other']
  const [reason, setReason] = useState(reasons[0])
  const [notify, setNotify] = useState(true)
  if (!o) return null
  const willRefund = o.status === 'paid'

  return (
    <OrdModalShell onClose={onClose}>
      <div style={{ padding: '22px 24px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <span className="icon-tile danger" style={{ width: 40, height: 40, fontSize: 18 }}><I.ban /></span>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 600 }}>Cancel order {shortId(o.id)}?</h2>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{o.customer_name || 'Guest'} · {money(o.amount_total, o.currency)}</div>
          </div>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-soft)', lineHeight: 1.55 }}>
          This marks the order as cancelled.{willRefund ? <> The customer's payment of <strong style={{ color: 'var(--text)' }}>{money(o.amount_total, o.currency)}</strong> will be refunded in full.</> : ' No payment has been taken, so nothing will be charged.'}
        </p>

        <div style={{ marginTop: 16 }}>
          <label style={ordFieldLabel}>Reason for cancelling</label>
          <select className="input" value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: '100%' }}>
            {reasons.map(r => <option key={r}>{r}</option>)}
          </select>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, cursor: 'pointer', fontSize: 12.5, color: 'var(--text-soft)' }}>
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} />
          Email the customer to let them know
        </label>
      </div>
      <div style={{ display: 'flex', gap: 10, padding: '20px 24px', marginTop: 6 }}>
        <button className="btn" style={{ flex: 1 }} onClick={onClose} disabled={busy}>Keep order</button>
        <button className="btn" style={{ flex: 1, ...ordDangerSolid }} disabled={busy} onClick={() => onConfirm(o.id, reason)}><I.ban /> {busy ? 'Cancelling…' : 'Cancel order'}</button>
      </div>
    </OrdModalShell>
  )
}

/* ============ Refund modal ============ */
function RefundModal({ o, onClose, onConfirm, busy }) {
  if (!o) return null
  const totalPence = Number(o.amount_total) || 0
  const totalMajor = totalPence / 100
  const reasons = ['Order was wrong / missing items', 'Food quality issue', 'Late or never arrived', 'Customer changed mind', 'Goodwill gesture', 'Other']
  const [mode, setMode] = useState('full')
  const [amount, setAmount] = useState(totalMajor.toFixed(2))
  const [reason, setReason] = useState(reasons[0])

  const amtMajor = mode === 'full' ? totalMajor : Math.min(parseFloat(amount) || 0, totalMajor)
  const amtPence = Math.round(amtMajor * 100)

  return (
    <OrdModalShell onClose={onClose}>
      <div style={{ padding: '22px 24px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <span className="icon-tile" style={{ width: 40, height: 40, fontSize: 18 }}><I.refund /></span>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 600 }}>Refund order {shortId(o.id)}</h2>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{o.customer_name || 'Guest'} · card payment</div>
          </div>
        </div>

        {/* Full vs partial */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 4 }}>
          {[
            { id: 'full', label: 'Full refund', sub: money(totalPence, o.currency) },
            { id: 'partial', label: 'Partial', sub: 'Set amount' },
          ].map(opt => {
            const on = mode === opt.id
            return (
              <button key={opt.id} onClick={() => { setMode(opt.id); if (opt.id === 'full') setAmount(totalMajor.toFixed(2)) }}
                style={{
                  textAlign: 'left', padding: '11px 13px', borderRadius: 11,
                  border: `1.5px solid ${on ? 'var(--border-accent)' : 'var(--border)'}`,
                  background: on ? 'var(--accent-soft)' : 'var(--surface)', transition: 'all 0.14s ease',
                }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{opt.label}</div>
                <div style={{ fontSize: 11.5, color: on ? 'var(--accent-3)' : 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{opt.sub}</div>
              </button>
            )
          })}
        </div>

        {mode === 'partial' && (
          <div style={{ marginTop: 12 }}>
            <label style={ordFieldLabel}>Refund amount</label>
            <input className="input" type="number" min="0" max={totalMajor} step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)} style={{ width: '100%', fontFamily: 'var(--font-mono)' }} />
            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 5 }}>Up to {money(totalPence, o.currency)}</div>
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <label style={ordFieldLabel}>Reason</label>
          <select className="input" value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: '100%' }}>
            {reasons.map(r => <option key={r}>{r}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '12px 14px', borderRadius: 11, background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Customer will receive</span>
          <span style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{money(amtPence, o.currency)}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, padding: '20px 24px' }}>
        <button className="btn" style={{ flex: 1 }} onClick={onClose} disabled={busy}>Back</button>
        <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy || amtPence <= 0}
          onClick={() => onConfirm(o.id, mode === 'full' ? undefined : amtMajor, reason)}>
          <I.refund /> {busy ? 'Refunding…' : `Refund ${money(amtPence, o.currency)}`}
        </button>
      </div>
    </OrdModalShell>
  )
}

const ordFieldLabel = { display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-soft)', marginBottom: 6 }
const ordDangerSolid = { background: 'var(--danger)', color: 'white', borderColor: 'transparent' }

export default ClientOrders
